import React, { useState } from 'react';
import { Stack, Box, Typography, IconButton, Collapse, Grid, Paper } from '@mui/material';
import PageHeading from '../../Components/PageHeading';
import { useSelector } from 'react-redux';
import { AppColors } from '../../Utils/helper/AppColors';
import { AddIcon, MailIcon, SubstractIcon, SupportIcon } from '../../Components/Icons/Icons';
import { faqs } from '../../Utils/faqs';
import { getColorSettings } from '../../Utils/helper/commonUtils';
import { calc } from 'antd/es/theme/internal';
import CustomTag from '../../Components/CustomTag';
import { theme } from 'antd';

const Support = () => {
	const [openFaq, setOpenFaq] = useState({});
	// console.log('faqs', openFaq);
	const handleFaqToggle = (sectionIdx, qIdx) => {
		setOpenFaq((prev) => ({
			...prev,
			[`${sectionIdx}-${qIdx}`]: !prev[`${sectionIdx}-${qIdx}`],
		}));
	};

	const localSettingColors = getColorSettings();

	return (
		<Stack sx={{ padding: 2 }}>
			<PageHeading
				title="Support Center"
				subTitle="Get help with your account, find answers to common questions, or contact our support team directly."
				subTitleSize={16}
				titleSx={{
					fontSize: 24,
					fontWeight: 600,
					lineHeight: '130%',
				}}
			/>
			<Stack
				
				sx={(theme) => ({
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'column', md: 'row' },
					mt: 2,
					bgcolor: '#fff',
					border: `1px solid ${theme.palette.primary.lighter_shade}`,
					borderRadius: '15px',
					boxShadow: 1,
					padding: 3,
					minHeight: `calc(100vh - 120px)`,
					gap: 2, // gap between columns
					alignItems: 'flex-start',
				})}
			>
				{/* Left: FAQ List */}
				<Box
					sx={(theme) => ({
                        
						flex: { xs: 12,sm : 12 ,md: 7.5},
						maxHeight: `calc(100vh - 170px)`,
						overflowY: 'auto',
						padding: '16px 4px 16px 16px',
						borderRadius: '10px',
						border: `1px solid ${theme.palette.primary.light_shade}`,
						minWidth: 0, // for proper flex shrink
					})}
				>
					<Stack sx={{ mb: 3 }}>
						<Stack direction="row" alignItems={'center'} spacing={1}>
							<SupportIcon size={20} color={localSettingColors?.buttonBackground} />
							<Typography
								sx={{
									color: localSettingColors?.buttonBackground,
									fontWeight: 600,
									fontSize: 20,
									lineHeight: '130%',
								}}
							>
								Frequently Asked Questions
							</Typography>
						</Stack>
						<Typography sx={{ color: AppColors.HEADING_TITLE2, fontSize: 14, fontWeight: 400, lineHeight: '150%' }}>
							Quick answers to common questions about our platform
						</Typography>
					</Stack>

					{faqs.map((section, sectionIdx) => (
						<Box key={section.header} sx={{ mb: 4 }}>
							<Stack direction="row" alignItems={'center'} gap={3} sx={{ mb: 2 }}>
								<Typography
									sx={{
										color: AppColors.BLACK,
										fontWeight: 600,
										fontSize: 20,
										lineHeight: '130%',
									}}
								>
									{section.header}
								</Typography>
								<CustomTag
									tagTitle={`${section?.questions?.length} Questions`}
									pX={'16px'}
									pY={'6px'}
									fontSize={10}
									fontWeight={400}
									lineHeight={'121%'}
								/>
							</Stack>
							{section.questions.map((q, qIdx) => (
								<Box
									key={q.no}
									sx={(theme) => ({
										borderBottom: `1px solid ${theme.palette.primary.light_shade}}`,
										mb: 0,
									})}
								>
									<Stack
										direction="row"
										alignItems="center"
										justifyContent="space-between"
										sx={{ cursor: 'pointer', py: 1.5 }}
										onClick={() => handleFaqToggle(sectionIdx, qIdx)}
									>
										<Typography sx={{ fontWeight: 600, color: AppColors.BLACK, fontSize: 18, lineHeight: '150%' }}>
											{q.question}
										</Typography>
										<IconButton size="small" sx={{ ml: 1 }}>
											{openFaq[`${sectionIdx}-${qIdx}`] ? (
												<SubstractIcon color={localSettingColors?.fontColor} />
											) : (
												<AddIcon color={localSettingColors?.fontColor} />
											)}
										</IconButton>
									</Stack>
									<Collapse in={!!openFaq[`${sectionIdx}-${qIdx}`]}>
										<Typography sx={{ color: AppColors.BLACK, fontSize: 16, lineHeight: '150%', fontWeight: 400, pb: 1 }}>
											{q.answer}
										</Typography>
									</Collapse>
								</Box>
							))}
						</Box>
					))}
				</Box>
				{/* Right: Content Box */}
				<Box
					sx={(theme) => ({
						flex: { xs: 12 , sm: 12 , md: 4.5},
						border: `1px solid ${theme.palette.primary.light_shade}`,
						borderRadius: '10px',
						background: AppColors.BACKGROUND,
						minWidth: 0,
					})}
				>
					<Paper
						elevation={0}
						sx={{
							width: '100%',
							padding: 3,
							borderRadius: 2,
							background: AppColors.BACKGROUND,
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
						}}
					>
						<Stack direction={'row'} alignItems={'center'} spacing={1.5}>
							<MailIcon size={20} color={AppColors.HEADING_TITLE3} />
							<Typography vsx={{ fontWeight: 600, fontSize: 20, lineHeight: '130%' }}>Direct Contact</Typography>
						</Stack>

						<Paper
							elevation={0}
							sx={(theme) => ({
								padding: 2,
								borderRadius: '5px',
								background: theme.palette.primary.lightest_shade,
								border: `1px solid ${theme.palette.primary.light_shade}`,
							})}
						>
							<Typography
								sx={(theme) => ({
									color: theme.palette.primary.darker_primary,
									fontSize: 16,
									lineHeight: '150%',
									fontWeight: 400,
								})}
							>
								Our support team typically responds within 24 business hours. For urgent issues, please include "URGENT" in your
								email subject line.{' '}
							</Typography>
						</Paper>

						<Paper
							elevation={0}
							sx={(theme) => ({
								padding: 2,
								borderRadius: '5px',
								background: theme.palette.primary.lightest_shade,
								border: `1px solid ${theme.palette.primary.light_shade}`,
							})}
						>
							<Typography
								sx={(theme) => ({
									color: theme.palette.primary.darker_primary,
									fontSize: 16,
									lineHeight: '150%',
									fontWeight: 400,
									display: 'flex',
									gap: '4px',
								})}
							>
								<Typography sx={{ color: AppColors.BLACK }}>Email us at: </Typography>{' '}
								<Box component={'span'} sx={{ cursor: 'pointer' }}>
									support@cultureos.company.
								</Box>
							</Typography>
						</Paper>
					</Paper>
				</Box>
			</Stack>
		</Stack>
	);
};

export default Support;
